import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { PageHeader, Card, CardHeader, Button, Badge, Field, Input, Select, Spinner, EmptyState, ErrorText } from "../../components/ui";
import { date } from "../../lib/format";
import type { ClientRow, Page } from "../../lib/types";

export function CrmClients() {
  const { data, loading, reload } = useApi<Page<ClientRow>>("/api/crm/clients?limit=100");
  const [open, setOpen] = useState(false);
  const [inviteFor, setInviteFor] = useState<ClientRow | null>(null);

  return (
    <>
      <PageHeader title="Clientes" action={<Button onClick={() => setOpen((v) => !v)}>{open ? "Cerrar" : "Nuevo cliente"}</Button>} />
      {open && <CreateClient onDone={() => { setOpen(false); void reload(); }} />}
      {loading ? (
        <Spinner />
      ) : (data?.data.length ?? 0) === 0 ? (
        <EmptyState>No hay clientes.</EmptyState>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-5 py-3 font-medium">Nombre</th>
                {/* Secondary columns are hidden on phones so the row action stays on screen. */}
                <th className="hidden px-5 py-3 font-medium sm:table-cell">Tipo</th>
                <th className="hidden px-5 py-3 font-medium sm:table-cell">Contacto</th>
                <th className="hidden px-5 py-3 font-medium sm:table-cell">NIF/CIF</th>
                <th className="hidden px-5 py-3 font-medium sm:table-cell">Alta</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data?.data.map((c) => (
                <tr key={c.id} className="hover:bg-canvas">
                  <td className="px-5 py-3 font-medium">
                    {c.displayName}
                    {/* Phones: contact shown under the name instead of in its own column. */}
                    <div className="max-w-44 truncate text-xs font-normal text-muted sm:hidden">{c.email ?? c.phone ?? "—"}</div>
                  </td>
                  <td className="hidden px-5 py-3 text-muted sm:table-cell">{c.type === "COMPANY" ? "Empresa" : "Particular"}</td>
                  <td className="hidden px-5 py-3 text-muted sm:table-cell">{c.email ?? c.phone ?? "—"}</td>
                  <td className="hidden px-5 py-3 text-muted sm:table-cell">{c.taxId ?? "—"}</td>
                  <td className="hidden px-5 py-3 text-muted sm:table-cell">{date(c.createdAt)}</td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" onClick={() => setInviteFor(c)}>Invitar al portal</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {inviteFor && <InviteCustomer client={inviteFor} onClose={() => setInviteFor(null)} />}
    </>
  );
}

interface Invitation { id: string; email: string; status: "PENDING" | "ACCEPTED" | "REVOKED" | "EXPIRED"; expiresAt: number; createdAt: number }
const INV_TONE: Record<Invitation["status"], "ok" | "warn" | "danger" | "neutral"> = { PENDING: "warn", ACCEPTED: "ok", REVOKED: "danger", EXPIRED: "neutral" };
const INV_LABEL: Record<Invitation["status"], string> = { PENDING: "Pendiente", ACCEPTED: "Aceptada", REVOKED: "Revocada", EXPIRED: "Caducada" };

function InviteCustomer({ client, onClose }: { client: ClientRow; onClose: () => void }) {
  const { data, loading, reload } = useApi<{ data: Invitation[] }>(`/api/invitations?clientId=${client.id}`);
  const [email, setEmail] = useState(client.email ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ devToken?: string }>("/api/invitations", { email: email.trim(), role: "CUSTOMER", clientId: client.id });
      setDevLink(res.devToken ? `/accept-invitation?token=${res.devToken}` : null);
      setEmail("");
      await reload();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "No se pudo enviar la invitación.");
    } finally {
      setBusy(false);
    }
  }
  async function resend(id: string) {
    const res = await api.post<{ devToken?: string }>(`/api/invitations/${id}/resend`);
    setDevLink(res.devToken ? `/accept-invitation?token=${res.devToken}` : null);
    await reload();
  }
  async function revoke(id: string) {
    await api.post(`/api/invitations/${id}/revoke`);
    await reload();
  }

  const invitations = data?.data ?? [];

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="mx-auto my-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Acceso al portal</h2>
            <button onClick={onClose} className="text-muted">✕</button>
          </div>
          <p className="mt-1 mb-4 text-sm text-muted">Cliente: <b className="text-ink">{client.displayName}</b></p>

          {loading ? (
            <Spinner />
          ) : invitations.length > 0 ? (
            <ul className="mb-4 divide-y divide-line rounded-lg border border-line">
              {invitations.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                  <span className="truncate">{inv.email}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Badge tone={INV_TONE[inv.status]}>{INV_LABEL[inv.status]}</Badge>
                    {(inv.status === "PENDING" || inv.status === "EXPIRED") && (
                      <>
                        <button onClick={() => void resend(inv.id)} className="text-xs font-medium text-accent-600 hover:underline">Reenviar</button>
                        <button onClick={() => void revoke(inv.id)} className="text-xs font-medium text-muted hover:text-danger hover:underline">Revocar</button>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-4 text-sm text-muted">Este cliente todavía no tiene acceso al portal.</p>
          )}

          {!client.hasPaid && (
            <div className="mb-4 rounded-lg bg-warn-050 px-3 py-3 text-sm text-warn">
              Este cliente aún no ha pagado. El acceso al portal se habilita cuando registres el pago (SEPA/tarjeta) en uno de sus expedientes.
            </div>
          )}
          <form onSubmit={(e) => void send(e)} className="flex flex-col gap-3 border-t border-line pt-4">
            {error && <ErrorText>{error}</ErrorText>}
            <Field label="Invitar por correo" hint="Le enviaremos un email con el enlace para crear su cuenta (caduca en 48h).">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="cliente@ejemplo.com" disabled={!client.hasPaid} />
            </Field>
            {devLink && (
              <div className="rounded-lg border border-line bg-canvas px-3 py-2 text-xs">
                <div className="mb-1 font-medium text-muted">Enlace de prueba (solo en desarrollo, el email no se envía de verdad):</div>
                <a className="break-all font-mono text-accent-600 hover:underline" href={devLink}>{devLink}</a>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cerrar</Button>
              <Button type="submit" disabled={busy || !email || !client.hasPaid}>{busy ? "Enviando…" : "Enviar invitación"}</Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function CreateClient({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ type: "COMPANY", displayName: "", legalName: "", taxId: "", email: "", phone: "", address: "", country: "" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/crm/clients", {
        type: f.type,
        displayName: f.displayName.trim(),
        legalName: f.legalName.trim() || undefined,
        taxId: f.taxId.trim() || undefined,
        email: f.email.trim() || undefined,
        phone: f.phone.trim() || undefined,
        address: f.address.trim() || undefined,
        country: f.country.trim() ? f.country.trim().toUpperCase() : undefined,
      });
      onDone();
    } catch {
      setError("No se pudo crear el cliente. Revisa el email.");
    } finally {
      setBusy(false);
    }
  }

  const isCompany = f.type === "COMPANY";
  return (
    <Card className="mb-6">
      <CardHeader title="Nuevo cliente" />
      <form onSubmit={(e) => void submit(e)} className="grid gap-4 p-5 sm:grid-cols-2">
        {error && <div className="sm:col-span-2"><ErrorText>{error}</ErrorText></div>}
        <Field label="Tipo">
          <Select value={f.type} onChange={set("type")}>
            <option value="COMPANY">Empresa</option>
            <option value="INDIVIDUAL">Particular</option>
          </Select>
        </Field>
        <Field label={isCompany ? "Nombre comercial" : "Nombre completo"}>
          <Input value={f.displayName} onChange={set("displayName")} required autoFocus />
        </Field>
        <Field label={isCompany ? "Razón social (nombre fiscal)" : "Nombre fiscal"} hint="Opcional">
          <Input value={f.legalName} onChange={set("legalName")} />
        </Field>
        <Field label="NIF / CIF / VAT" hint="Dato fiscal">
          <Input value={f.taxId} onChange={set("taxId")} />
        </Field>
        <Field label="Email">
          <Input type="email" value={f.email} onChange={set("email")} placeholder="contacto@ejemplo.com" />
        </Field>
        <Field label="Teléfono">
          <Input value={f.phone} onChange={set("phone")} placeholder="+34 …" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Dirección (fiscal / postal)">
            <Input value={f.address} onChange={set("address")} placeholder="Calle, nº, CP, ciudad, país" />
          </Field>
        </div>
        <Field label="País (opcional, ISO-2)" hint="Se puede dejar en blanco">
          <Input value={f.country} onChange={set("country")} maxLength={2} placeholder="ES" />
        </Field>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={busy || !f.displayName.trim()}>{busy ? "Guardando…" : "Crear cliente"}</Button>
        </div>
      </form>
    </Card>
  );
}
